'use client';

import {
  animate,
  type AnimationPlaybackControls,
  type Transition,
  type Variants,
} from 'framer-motion';
import { useCallback, useLayoutEffect, useRef } from 'react';

import type { TopicsStep } from './topics-types';

const EASE = [0.22, 1, 0.36, 1] as const;

/** One curve for the whole swap: the card ↔ „Téma“ bar morph and the height of the section. */
export const SWAP_TRANSITION: Transition = { duration: 0.45, ease: EASE };

/** Variant labels the step containers hand down to their parts. */
export const STEP_LABELS = { initial: 'enter', animate: 'idle', exit: 'leave' } as const;

/**
 * A part that only one step has (texts, radios, fields, frames): it fades in once the morph is on
 * its way and leaves at once. The shared parts (topic background, icon and title) carry no variants –
 * their `layoutId` morph crossfades them.
 */
export const fadePart: Variants = {
  enter: { opacity: 0 },
  idle: { opacity: 1, transition: { duration: 0.3, delay: 0.12 } },
  leave: { opacity: 0, transition: { duration: 0.18 } },
};

/** `fadePart` with an 12 px rise – the form rows and the confirmation. */
export const risePart: Variants = {
  enter: { opacity: 0, y: 12 },
  idle: { opacity: 1, y: 0, transition: { duration: 0.35, delay: 0.14, ease: EASE } },
  leave: { opacity: 0, transition: { duration: 0.18 } },
};

/**
 * The leaving step stays in the tree until the morph has ended (its shared parts are the start of
 * the morph) and then fades; towards the confirmation nothing morphs, so the form fades at once.
 * `custom` = the step that comes next (AnimatePresence `custom`).
 */
export const stepVariants: Variants = {
  leave: (next: TopicsStep) =>
    next === 'sent'
      ? { opacity: 0, transition: { duration: 0.2 } }
      : { opacity: 0, transition: { delay: 0.3, duration: 0.15 } },
};

/**
 * Animates the height of the swap area between the steps. `capture()` right before the step changes
 * remembers the current height; after the commit the new step's height is measured (the leaving
 * step is already popped out of the flow by `AnimatePresence mode="popLayout"`) and the outer box
 * animates between the two, clipped, then goes back to `auto` (errors, resizes). Without motion
 * (`reduce`) the height just changes.
 */
export function useSwapHeight(step: TopicsStep, reduce: boolean) {
  const outerRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const from = useRef<number | null>(null);
  const running = useRef<AnimationPlaybackControls | null>(null);

  const capture = useCallback(() => {
    from.current = outerRef.current?.offsetHeight ?? null;
  }, []);

  useLayoutEffect(() => {
    const outer = outerRef.current;
    const inner = innerRef.current;
    const start = from.current;
    from.current = null;
    if (!outer || !inner || start === null) return;

    running.current?.stop();
    running.current = null;
    const release = () => {
      outer.style.height = '';
      outer.style.overflow = '';
    };
    const end = inner.offsetHeight;
    if (reduce || start === end) return release();

    // The first keyframe is applied on the next animation frame – pin it now, before the paint.
    outer.style.height = `${start}px`;
    outer.style.overflow = 'hidden';
    const controls = animate(outer, { height: [start, end] }, SWAP_TRANSITION);
    running.current = controls;
    void controls.then(() => {
      if (running.current !== controls) return;
      running.current = null;
      release();
    });
  }, [step, reduce]);

  return { outerRef, innerRef, capture };
}
